import {
  ComponentProps,
  ElementType,
  FormEvent,
  forwardRef,
  useMemo,
} from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';

import classNames from 'classnames';

import { Dialog } from '@backstage/ui';

import styles from './ContentBui.module.css';
import { Context, DialogContext, useDialogContext } from './context';

// This is a synthetic form event that can have preventDefault called on it.
// This is used to prevent the dialog from closing when a form is submitted.
class SyntheticFormEvent extends Event {
  private _defaultPrevented: boolean = false;
  public submitter?: HTMLElement;

  constructor(
    type: string,
    eventInitDict?: EventInit & { submitter?: HTMLElement },
  ) {
    super(type, eventInitDict);
    this.submitter = eventInitDict?.submitter;
  }

  preventDefault(): void {
    this._defaultPrevented = true;
  }

  isPreventDefault(): boolean {
    return this._defaultPrevented;
  }
}

// MUI v4 Dialog maxWidth breakpoints resolved to pixels, so the BUI `width`
// prop reproduces the same dialog sizing.
const MAX_WIDTH_PX: Record<string, number> = {
  xs: 444,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

export type ContentBuiProps = {
  isLoading?: boolean;
  component?: ElementType;
  className?: string;
} & Omit<
  ComponentProps<ElementType>,
  'className' | 'children' | 'classes' | 'onSubmit'
> & {
    onSubmit?: (event: FormEvent<HTMLElement>) => void | Promise<void>;
  };

export const ContentBui = forwardRef<HTMLDivElement, ContentBuiProps>(
  (props, ref) => {
    const {
      id,
      open,
      onClose,
      props: dialogProps,
      static: staticProp,
      onOpen,
      isLoading,
    } = useDialogContext();

    const {
      component: Component = 'div',
      className,
      onSubmit,
      ...rest
    } = props;

    const [{ loading, error }, handleSubmit] = useAsyncFn(
      async (event: FormEvent<HTMLElement>) => {
        event.preventDefault();

        // Capture the submitter before creating the synthetic event
        const nativeEvent = event.nativeEvent as SubmitEvent;
        const submitter = nativeEvent?.submitter ?? undefined;

        // Must use a synthetic event here, because we must prevent the actual
        // form event directly, otherwise the page will reload as soon as we
        // call onSubmit and it starts doing it's business.
        const syntheticEvent = new SyntheticFormEvent('submit', {
          bubbles: true,
          cancelable: true,
          submitter,
        });

        await onSubmit?.(syntheticEvent);

        const isPrevented = syntheticEvent.isPreventDefault();
        if (!isPrevented) {
          onClose();
        }
      },
      [onSubmit, onClose],
    );

    const value: Context = useMemo(
      () => ({
        id,
        isLoading: loading || isLoading,
        error,
        open,
        onClose,
        props: dialogProps,
        static: staticProp,
        onOpen,
      }),
      [
        id,
        loading,
        isLoading,
        error,
        open,
        onClose,
        dialogProps,
        staticProp,
        onOpen,
      ],
    );

    const content = (
      <Component
        {...rest}
        onSubmit={Component === 'form' ? handleSubmit : undefined}
        ref={ref}
        className={classNames(styles.component, className)}
      />
    );

    // if "static" is true, the content is not rendered inside a dialog modal.
    if (staticProp) {
      return (
        <DialogContext.Provider value={value}>{content}</DialogContext.Provider>
      );
    }

    const fullScreen = Boolean(dialogProps?.fullScreen);
    const maxWidth = dialogProps?.maxWidth;
    const mappedWidth =
      typeof maxWidth === 'string' ? MAX_WIDTH_PX[maxWidth] : undefined;
    const width = fullScreen ? '100%' : mappedWidth;
    const height = fullScreen ? '100%' : undefined;

    return (
      <Dialog
        isOpen={open}
        onOpenChange={isOpen => {
          if (!isOpen) {
            onClose();
          }
        }}
        aria-labelledby={`${id}-title`}
        width={width}
        height={height}
      >
        <DialogContext.Provider value={value}>{content}</DialogContext.Provider>
      </Dialog>
    );
  },
);

ContentBui.displayName = 'DialogCompositeBui.Content';

export default ContentBui;
